/**
 * BUNDLE VARIANT AVAILABILITY
 *
 * On a bundle product each option is a separate component product, so whether a
 * flavour is choosable depends on that component's own stock — not on the
 * bundle's variant matrix.
 *
 * Liquid already renders the correct state (see the component_product branch in
 * snippets/product-variant-options.liquid, which stamps data-component-available
 * on every radio). The problem is global.js: VariantSelects.updateVariantStatuses
 * re-derives availability from the bundle's variant list after every change and
 * overwrites options 2 and 3. It is a base theme file we do not modify, so this
 * component restores the component-derived state instead.
 *
 * Wraps <variant-radios>. Listening on the wrapper means the handler runs after
 * the one variant-radios binds on itself, and the rAF pushes it past the
 * synchronous work inside onVariantChange.
 */
if (!customElements.get('bundle-variant-availability')) {
  customElements.define(
    'bundle-variant-availability',
    class BundleVariantAvailability extends HTMLElement {
      connectedCallback() {
        this.clickable = this.dataset.unavailableOptionsClickable === 'true';
        this.schedule = this.schedule.bind(this);
        this.addEventListener('change', this.schedule);

        // Replace the recompute on this one <variant-radios> instance rather
        // than reacting to it. An own property shadows the prototype method and
        // survives custom-element upgrade, so this holds however the scripts
        // happen to be ordered — and it removes the flicker that reacting after
        // the fact could not avoid. global.js itself is untouched, and every
        // other variant picker on the site keeps Dawn's behaviour.
        const radios = this.querySelector('variant-radios');
        if (radios) radios.updateVariantStatuses = () => this.apply();

        // A change listener alone is not enough: selecting an unavailable option
        // sends global.js down its setUnavailable path, which rewrites the
        // classes again after the section re-render resolves — later than any
        // frame we could schedule from the change event. Watching the attributes
        // catches every pass, whenever it lands.
        this.observer = new MutationObserver(this.schedule);
        this.observer.observe(this, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'disabled'],
        });

        this.apply();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.schedule);
        if (this.observer) this.observer.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      // apply() only writes when a value actually differs, so the mutations it
      // causes settle after one extra pass rather than looping.
      schedule() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.apply());
      }

      apply() {
        this.querySelectorAll('input[data-component-available]').forEach((input) => {
          const available = input.dataset.componentAvailable === 'true';

          // Mirror global.js: when unavailable options stay clickable they keep
          // the always-clickable class instead of the disabled attribute, so the
          // shopper can still select them and read the resulting message.
          const wantDisabledClass = !available;
          const wantAlwaysClickable = !available && this.clickable;
          const wantDisabledAttr = !available && !this.clickable;

          input.classList.toggle('disabled', wantDisabledClass);
          input.classList.toggle('always-clickable', wantAlwaysClickable);
          if (input.disabled !== wantDisabledAttr) input.disabled = wantDisabledAttr;
        });
      }
    }
  );
}
